import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ClubRegister.css";

function ClubRegistrationForm() {
  const navigate = useNavigate();

  // Helper to format city (capitalize each word) and state (uppercase)
  const formatCity = (city) => {
    if (!city) return "";
    return city.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const formatState = (state) => {
    if (!state) return "";
    return state.toUpperCase();
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [error, setError] = useState("");

  // Token validation state
  const [token, setToken] = useState("");
  const [tokenValidated, setTokenValidated] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [validatingToken, setValidatingToken] = useState(false);

  // Token request modal state
  const [showTokenRequestModal, setShowTokenRequestModal] = useState(false);
  const [tokenRequestData, setTokenRequestData] = useState({
    name: "",
    position: "",
    email: "",
  });
  const [tokenRequestSubmitted, setTokenRequestSubmitted] = useState(false);
  const [tokenRequestError, setTokenRequestError] = useState("");

  const baseURL = "https://lionfish-app-w73zu.ondigitalocean.app/api/";

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 2) {
      try {
        const response = await axios.get(`${baseURL}myclub/search-golf-clubs/?query=${query}`);
        setSearchResults(response.data.results);
      } catch (error) {
        console.error("Search API Error:", error);
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectClub = (club) => {
    setSelectedClub(club);
    setSearchQuery(club.name);
    setSearchResults([]);
    // Reset token state when selecting a new club
    setToken("");
    setTokenValidated(false);
    setTokenError("");
  };

  const handleClearSelection = () => {
    setSelectedClub(null);
    setSearchQuery("");
    setToken("");
    setTokenValidated(false);
    setTokenError("");
  };

  const handleValidateToken = async () => {
    if (!token.trim()) {
      setTokenError("Please enter a verification token.");
      return;
    }

    setValidatingToken(true);
    setTokenError("");

    try {
      const response = await axios.post(`${baseURL}myclub/validate-token/`, {
        club_id: selectedClub.club_id,
        token: token.trim().toUpperCase(),
      });

      if (response.data.valid) {
        setTokenValidated(true);
        setTokenError("");
      } else {
        setTokenError(response.data.error || "Invalid token.");
      }
    } catch (error) {
      setTokenError(error.response?.data?.error || "Error validating token.");
    } finally {
      setValidatingToken(false);
    }
  };

  const handleTokenRequestChange = (e) => {
    setTokenRequestData({
      ...tokenRequestData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmitTokenRequest = async (e) => {
    e.preventDefault();
    setTokenRequestError("");

    try {
      const response = await axios.post(`${baseURL}myclub/request-token/`, {
        club_id: selectedClub.club_id,
        ...tokenRequestData,
      });

      if (response.data.success) {
        setTokenRequestSubmitted(true);
      } else {
        setTokenRequestError(response.data.error || "Error submitting request.");
      }
    } catch (error) {
      setTokenRequestError(error.response?.data?.error || "Error submitting request.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClub) {
      setError("Please select a club.");
      return;
    }

    if (!tokenValidated) {
      setError("Please validate your verification token first.");
      return;
    }

    const userFormData = {
      username: e.target.username.value,
      email: e.target.email.value,
      password: e.target.password.value,
      confirm_password: e.target.confirm_password.value,
      selected_club_id: selectedClub.club_id,
      token: token.trim().toUpperCase(),
    };

    try {
      await axios.post(`${baseURL}myclub/register/`, userFormData);
      alert("Registration successful!");
      navigate("/myclub/login/");
    } catch (error) {
      console.error("Registration Error:", error.response?.data || error.message);
      setError(error.response?.data?.error || error.response?.data?.errors || "An error occurred.");
    }
  };

  return (
    <div className="club-register-container">
      <h1>Club Registration</h1>

      {/* Step 1: Search and Select Club */}
      {!selectedClub && (
        <>
          <p className="cr-helper-text">
            Search for your club to get started
          </p>
          <div className="cr-search-wrapper">
            <input
              type="text"
              placeholder="Search Club Name"
              value={searchQuery}
              onChange={handleSearch}
              autoComplete="off"
            />
            {searchResults.length > 0 && (
              <ul className="cr-search-results">
                {searchResults.map((club) => (
                  <li key={club.club_id} onClick={() => handleSelectClub(club)}>
                    {club.name} - {formatCity(club.city)}, {formatState(club.state)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <a href="mailto:support@privateiq.golf?subject=Club%20Not%20Listed" className="cr-add-club-link">
            Not Seeing Your Club?
          </a>
        </>
      )}

      {/* Step 2: Token Validation */}
      {selectedClub && !tokenValidated && (
        <div>
          <div className="cr-selected-club-card">
            <div className="cr-club-name">{selectedClub.name}</div>
            <span className="cr-club-location">
              {formatCity(selectedClub.city)}, {formatState(selectedClub.state)}
            </span>
            <button
              type="button"
              onClick={handleClearSelection}
              className="cr-btn-secondary"
            >
              Change
            </button>
          </div>

          <p className="cr-helper-text">
            Enter the verification token you received via email or phone
          </p>

          <input
            type="text"
            placeholder="Enter Verification Token (e.g., ABC12345)"
            value={token}
            onChange={(e) => setToken(e.target.value.toUpperCase())}
            style={{ textTransform: "uppercase" }}
            maxLength={8}
          />

          {tokenError && <div className="cr-error-text">{tokenError}</div>}

          <button
            type="button"
            onClick={handleValidateToken}
            disabled={validatingToken}
          >
            {validatingToken ? "Validating..." : "Validate Token"}
          </button>

          <div className="cr-divider">
            <p className="cr-helper-text">
              Don't have a token?{" "}
              <button
                type="button"
                onClick={() => setShowTokenRequestModal(true)}
                className="cr-btn-link"
              >
                Request one here
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Step 3: Registration Form (only shown after token validation) */}
      {selectedClub && tokenValidated && (
        <>
          <div className="cr-verified-card">
            <div className="cr-verified-badge">Token Verified</div>
            <div>Registering for: <span className="cr-club-name">{selectedClub.name}</span></div>
          </div>

          <form onSubmit={handleSubmit}>
            <input name="username" type="text" placeholder="Username" required />
            <input name="email" type="email" placeholder="Email" required />
            <input name="password" type="password" placeholder="Password" required />
            <input name="confirm_password" type="password" placeholder="Confirm Password" required />
            <button type="submit">Complete Registration</button>
          </form>
        </>
      )}

      {error && <div className="cr-error-text">{typeof error === 'object' ? JSON.stringify(error) : error}</div>}

      {/* Token Request Modal */}
      {showTokenRequestModal && (
        <div className="cr-modal-overlay">
          <div className="cr-modal-content">
            {!tokenRequestSubmitted ? (
              <>
                <h2>Request Verification Token</h2>
                <p className="cr-helper-text">
                  Please provide your information and we'll verify your affiliation with {selectedClub?.name}.
                </p>

                <form onSubmit={handleSubmitTokenRequest}>
                  <input
                    name="name"
                    type="text"
                    placeholder="Your Full Name"
                    value={tokenRequestData.name}
                    onChange={handleTokenRequestChange}
                    required
                  />
                  <input
                    name="position"
                    type="text"
                    placeholder="Your Position (e.g., General Manager)"
                    value={tokenRequestData.position}
                    onChange={handleTokenRequestChange}
                    required
                  />
                  <input
                    name="email"
                    type="email"
                    placeholder="Your Email Address"
                    value={tokenRequestData.email}
                    onChange={handleTokenRequestChange}
                    required
                  />

                  {tokenRequestError && (
                    <div className="cr-error-text">{tokenRequestError}</div>
                  )}

                  <div className="cr-btn-row">
                    <button type="submit">Submit Request</button>
                    <button
                      type="button"
                      onClick={() => setShowTokenRequestModal(false)}
                      className="cr-btn-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="cr-success-title">Request Submitted!</h2>
                <p>
                  Thank you! We'll verify your affiliation with {selectedClub?.name} and contact you within 48 hours with your verification token.
                </p>
                <button
                  onClick={() => {
                    setShowTokenRequestModal(false);
                    setTokenRequestSubmitted(false);
                    setTokenRequestData({ name: "", position: "", email: "" });
                  }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClubRegistrationForm;
